'use client'

import NextImage from 'next/image'
import React from 'react'

import { cn } from '@/utilities/ui'
import { useImageSettings } from '@/components/Media/imageSettingsContext'

export type ThumbnailFallbackProps = {
  /** `sizes` do `next/image` (o componente é sempre `fill`). */
  sizes?: string
  /** Classes extra na imagem (ex.: `size-full`, hover scale). */
  imgClassName?: string
  /** Alt opcional; por defeito decorativo (placeholder genérico → `aria-hidden`). */
  alt?: string
}

/** `sizes` por defeito quando o call site não define (evita o warning de `fill` sem `sizes`). */
const DEFAULT_THUMB_SIZES = '(max-width: 768px) 100vw, 33vw'

/**
 * Fallback de miniatura para listagens de posts SEM imagem (`heroImage`/`meta.image` nulos).
 * Espelha a aba «Imagens» do «Identidade do site» via `useImageSettings()`:
 *  - `placeholderMode = 'image'` + `fallbackUrl` → renderiza a imagem padrão em `fill`;
 *  - `placeholderMode = 'gray'` (ou `fallbackUrl` vazio) → não renderiza nada; o contentor pai
 *    (sempre `relative … bg-muted`) já fornece o fundo cinza.
 * Não adiciona caixa de layout — é renderizado dentro do pai `relative` já existente.
 */
export function ThumbnailFallback({ sizes, imgClassName, alt = '' }: ThumbnailFallbackProps) {
  const { placeholderMode, fallbackUrl } = useImageSettings()

  if (placeholderMode !== 'image' || !fallbackUrl) return null

  return (
    <NextImage
      src={fallbackUrl}
      alt={alt}
      fill
      sizes={sizes ?? DEFAULT_THUMB_SIZES}
      quality={100}
      className={cn('object-cover', imgClassName)}
      decoding="async"
      aria-hidden={alt === '' ? true : undefined}
      // Imagem padrão custom (upload R2) → 403 no otimizador; servir direto. Local fica otimizado.
      unoptimized={/^https?:\/\//i.test(fallbackUrl)}
    />
  )
}
